import { useCallback, useEffect, useRef } from "react";

const SWIPE_THRESHOLD_PX = 40;

export default function useGalleryNavigation({
  isOpen,
  keyboard = true,
  swipe = true,
  onClose,
  onNext,
  onPrev,
}) {
  const touchStartXRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !keyboard) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onPrev?.();
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        onNext?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, keyboard, onClose, onNext, onPrev]);

  const onTouchStart = useCallback(
    (event) => {
      if (!isOpen || !swipe) {
        return;
      }

      touchStartXRef.current = event.changedTouches?.[0]?.clientX ?? null;
    },
    [isOpen, swipe],
  );

  const onTouchEnd = useCallback(
    (event) => {
      if (!isOpen || !swipe || touchStartXRef.current == null) {
        return;
      }

      const touchEndX =
        event.changedTouches?.[0]?.clientX ?? touchStartXRef.current;
      const deltaX = touchEndX - touchStartXRef.current;

      touchStartXRef.current = null;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) {
        return;
      }

      if (deltaX < 0) {
        onNext?.();
        return;
      }

      onPrev?.();
    },
    [isOpen, onNext, onPrev, swipe],
  );

  return {
    onTouchStart,
    onTouchEnd,
  };
}
